import { useNavigate, useParams } from 'react-router-dom';
import { useLazyQuery, useQuery } from '@apollo/client';
import { GET_CAR_BOOKINGS } from '../../../../services/bookingService/queries';
import styled from 'styled-components';
import tw from 'twin.macro';
import { formatInstantInZone } from '../../../../helpers/timezoneHelpers';
import {
  ICarBookingsQuery,
  useCarBookingsQuery,
} from '../../../../services/bookingService/__generated__/queries.generated';
import { URLS } from '../../../../../constants/urls';
import { GET_LOCATIONS_BY_HOST_ID } from '../../../../services/locationServices/queries';
import { IGetLocationsQuery } from '../../../../services/locationServices/__generated__/queries.generated';
import { useEffect, useState } from 'react';
import { BookingStatus, CarSwapStatus } from '../../../../../__generated__/graphql';
import LoadingSpinner from '../../../../components/LoadingSpinner';
import { formatAddress } from '../../../../helpers/formatAddress';

// Styled Components
const Container = styled.div`
  ${tw`w-full max-w-screen-xl mx-auto p-4`}
`;

const DateSection = styled.div`
  ${tw`w-full mb-6 bg-bg-secondary p-4 rounded-lg shadow-card`}
`;

const DateHeader = styled.h2`
  ${tw`text-base font-semibold mb-4 text-text-secondary`}
`;

const BookingCard = styled.div`
  ${tw`bg-white rounded-lg shadow-card mb-4 p-4 transition-shadow duration-200`}

  &:hover {
    ${tw`cursor-pointer`}
  }
`;

const EndingTime = styled.div`
  ${tw`text-red-500 text-sm mb-3`}
`;

const CarDetails = styled.div`
  ${tw`flex justify-between items-start gap-4`}
`;

const InfoSection = styled.div`
  ${tw`flex-1`}
`;

const CarName = styled.h3`
  ${tw`text-lg font-medium mb-2`}
`;

const LocationText = styled.p`
  ${tw`text-sm text-text-secondary mb-2`}
`;

const GuestSection = styled.div`
  ${tw`flex items-center`}
`;

const GuestAvatar = styled.div`
  ${tw`w-8 h-8 rounded-full bg-bg-secondary mr-2 flex items-center justify-center text-text-secondary text-xs`}
`;

const GuestId = styled.span`
  ${tw`text-sm text-text-secondary`}
`;

const CarImage = styled.img`
  ${tw`w-20 h-16 rounded-md object-cover flex-shrink-0`}
`;

const SwapIndicator = styled.div`
  ${tw`mt-2 p-2 bg-yellow-50 border border-yellow-200 rounded-md text-sm`}
`;

const SwapLabel = styled.span`
  ${tw`font-semibold text-yellow-800`}
`;

const OriginalCarName = styled.span`
  ${tw`text-yellow-900`}
`;

const LocationSection = ({
  pickupLocation,
  returnLocation,
}: {
  pickupLocation: IGetLocationsQuery['locations'][number];
  returnLocation: IGetLocationsQuery['locations'][number];
}) => {
  if (!pickupLocation || !returnLocation) return null;
  if (pickupLocation.id === returnLocation.id)
    return (
      <LocationText>
        Pickup and Return at:
        <br />
        {pickupLocation.city}, {pickupLocation.state} {pickupLocation.zipCode}
      </LocationText>
    );
  return (
    <LocationText>
      Pickup: {formatAddress(pickupLocation)}
      <br />
      Return: {formatAddress(returnLocation)}
    </LocationText>
  );
};

const BookingCardDetails = ({ booking }: { booking: ICarBookingsQuery['carBookings'][number] }) => {
  const [pickupLocation, setPickupLocation] = useState<IGetLocationsQuery['locations'][number]>(
    {} as IGetLocationsQuery['locations'][number],
  );
  const [returnLocation, setReturnLocation] = useState<IGetLocationsQuery['locations'][number]>(
    {} as IGetLocationsQuery['locations'][number],
  );
  const {
    data: locationsData,
    loading: locationsLoading,
    error: locationsError,
  } = useQuery<{ locationsByHostId: IGetLocationsQuery['locations'] }>(GET_LOCATIONS_BY_HOST_ID, {
    variables: { hostId: booking.hostId },
    skip: !booking.hostId,
    fetchPolicy: 'cache-first',
  });

  useEffect(() => {
    if (locationsData) {
      setPickupLocation(
        locationsData.locationsByHostId.find(
          location => location.id === booking.pickupLocationId,
        ) ?? ({} as IGetLocationsQuery['locations'][number]),
      );
      setReturnLocation(
        locationsData.locationsByHostId.find(
          location => location.id === booking.returnLocationId,
        ) ?? ({} as IGetLocationsQuery['locations'][number]),
      );
    }
  }, [locationsData]);

  return (
    <>
      <EndingTime>
        Ending at{' '}
        {formatInstantInZone(
          booking.endAt,
          booking.car?.location?.timezone ?? 'America/Los_Angeles',
          'yyyy-MM-dd HH:mm',
        )}
      </EndingTime>
      <CarDetails>
        <InfoSection>
          <CarName>
            {booking?.car?.make} {booking?.car?.model} {booking?.car?.year}
          </CarName>
          {(booking as any).swapHistory && (booking as any).swapHistory.length > 0 && (
            <SwapIndicator>
              <SwapLabel>
                {(booking as any).swapHistory.length === 1
                  ? 'Swapped 1 time'
                  : `Swapped ${(booking as any).swapHistory.length} times`}
                {(booking as any).originalCar && (
                  <>
                    {' '}
                    • Original: {(booking as any).originalCar.year}{' '}
                    {(booking as any).originalCar.make} {(booking as any).originalCar.model}
                  </>
                )}
              </SwapLabel>
            </SwapIndicator>
          )}
          <LocationSection pickupLocation={pickupLocation} returnLocation={returnLocation} />
          <GuestSection>
            <GuestId>{booking.guest?.displayName}</GuestId>
          </GuestSection>
        </InfoSection>
        <CarImage
          tw="w-20 h-16 rounded-md object-cover ml-4"
          src={`${URLS.S3_BUCKET.CAR_THUMBNAILS}/${booking?.car?.thumbnailUrl || ''}`}
          alt={`${booking?.car?.thumbnailUrl || ''}`}
        />
      </CarDetails>
    </>
  );
};

export default function BookingsByCar() {
  const navigate = useNavigate();
  const { carId } = useParams();
  const { data, loading, error } = useCarBookingsQuery({
    variables: { carId: carId || '', status: BookingStatus.Booked },
    fetchPolicy: 'cache-first',
    skip: !carId,
  });

  if (loading) return <LoadingSpinner fullScreen color="var(--color-black)" />;
  if (error) return <div>Error: {error?.message}</div>;

  // Group bookings by date
  const groupedBookings = data?.carBookings.reduce(
    (acc, booking) => {
      const date = formatInstantInZone(
        booking.startAt,
        booking.car?.location?.timezone ?? 'America/Los_Angeles',
        'EEEE, MMMM d, yyyy HH:mm',
      );
      if (!acc[date]) acc[date] = [];
      acc[date].push(booking);
      return acc;
    },
    {} as Record<string, typeof data.carBookings>,
  );
  if (!groupedBookings) return <div>No bookings found</div>;
  return (
    <Container>
      <h1 tw="text-2xl font-bold mb-6">Booked</h1>
      {Object.entries(groupedBookings).map(([date, bookings], index) => {
        return (
          <DateSection key={`${date}-${index}`}>
            <DateHeader>{date}</DateHeader>
            {bookings.map(booking => (
              <BookingCard
                key={booking.id}
                onClick={() => navigate(`/cars/host/booking/${booking.id}`)}
              >
                <BookingCardDetails booking={booking} />
              </BookingCard>
            ))}
          </DateSection>
        );
      })}
    </Container>
  );
}
